import React, { memo, ReactElement, useCallback } from "react";

//constants
import { VALID_RENDERING_DETAILS_TYPE } from "@core-ui/constants/RenderingDetails";

//types
import { RenderingDetails, RenderingDetailsSubDetails } from "@swiggy-private/cart-types";

//Components
import Display from "../bill-detail-item/display";
import { InfoDisplay } from "../bill-detail-item/info-display";
import { Separator } from "../bill-detail-item/separator";
import Title from "../bill-detail-item/title";
import { TransparentLine } from "../bill-detail-item/transparent-line";
import { NestedTooltipItem } from "./styles";

interface TooltipItemProps {
    itemData: RenderingDetails | RenderingDetailsSubDetails;
    onItemClick?: (itemType: string) => void;
    isNested?: boolean;
    isSuperItem?: (key: string) => boolean;
    isItemValueRupee?: (itemData: RenderingDetails) => boolean;
    isOneLiteItem?: (key: string) => boolean;
}

function TooltipItem({
    itemData,
    onItemClick,
    isNested = false,
    isSuperItem,
    isItemValueRupee,
    isOneLiteItem,
}: TooltipItemProps): ReactElement | null {
    // Render nested sub_details if they exist and this is an INFODISPLAY type
    const renderNestedSubDetails = useCallback(() => {
        if (itemData?.sub_details && itemData?.type?.toUpperCase() === VALID_RENDERING_DETAILS_TYPE.INFODISPLAY) {
            return (
                <NestedTooltipItem>
                    {Array.isArray(itemData?.sub_details)
                        ? itemData?.sub_details
                              .filter(
                                  (item: RenderingDetailsSubDetails) =>
                                      item?.type?.toUpperCase() === VALID_RENDERING_DETAILS_TYPE.INFODISPLAY,
                              )
                              .map((item: RenderingDetailsSubDetails, index: number) => (
                                  <TooltipItem
                                      key={`${item?.key || "nested"}_${index}`}
                                      itemData={item}
                                      onItemClick={onItemClick}
                                      isSuperItem={isSuperItem}
                                      isItemValueRupee={isItemValueRupee}
                                      isOneLiteItem={isOneLiteItem}
                                      isNested
                                  />
                              ))
                        : null}
                </NestedTooltipItem>
            );
        }
        return null;
    }, [itemData?.sub_details, itemData?.type, onItemClick, isNested, isSuperItem, isItemValueRupee, isOneLiteItem]);

    // Render the appropriate component based on the type
    const renderComponent = useCallback(() => {
        switch (itemData?.type?.toUpperCase()) {
            case VALID_RENDERING_DETAILS_TYPE.DISPLAY:
                return (
                    <Display
                        itemData={itemData as RenderingDetails}
                        isInTooltip
                        onItemClick={onItemClick}
                        isSuperItem={isSuperItem}
                        isItemValueRupee={isItemValueRupee}
                        isOneLiteItem={isOneLiteItem}
                    />
                );

            case VALID_RENDERING_DETAILS_TYPE.INFODISPLAY:
                return (
                    <>
                        <InfoDisplay isInTooltip isNested={isNested} itemData={itemData as RenderingDetails} />
                        {renderNestedSubDetails()}
                    </>
                );

            case VALID_RENDERING_DETAILS_TYPE.SEPARATOR:
                return <Separator isInTooltip />;

            case VALID_RENDERING_DETAILS_TYPE.TITLE:
                return <Title itemData={itemData as RenderingDetails} isInTooltip />;

            case VALID_RENDERING_DETAILS_TYPE.TRANSPARENT_SEPARATOR:
                return <TransparentLine />;

            default:
                return null;
        }
    }, [itemData?.type, itemData?.sub_details, onItemClick, isNested, isSuperItem, isItemValueRupee, isOneLiteItem]);

    // Wrap nested items with a class for proper margin
    return isNested ? <div>{renderComponent()}</div> : renderComponent();
}

export default memo(TooltipItem);
